import { MediaCarousel } from '@euler/app/components/media/MediaCarousel';
import { Tagline } from '@euler/app/components/Tagline';
import { Typography } from '@euler/app/components/typography';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import {
  buildTimeline,
  TimelineCell,
  TimelineEventCell,
  useTaskTimeline,
  useTaskTimelineModels,
} from '@euler/app/flows/task/functions/timeline';
import { TimelineSectionType } from '@euler/app/flows/task/functions/timeline/types';
import { Panel } from '@euler/app/tv/components/Panel';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { AnimationSource } from '@euler/assets';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { Icon, IconType } from '@euler/components/Icomoon';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { HStack } from '@euler/components/layout/Stack';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { MediaObject } from '@euler/model';
import { timeUtilNow } from '@euler/utils/formatters';
import { compareDesc } from 'date-fns';
import AnimatedLottieView from 'lottie-react-native';
import { MotiView } from 'moti';
import { memo, useMemo } from 'react';
import { FlatList, View } from 'react-native';

const isEventCell = (cell: TimelineCell): cell is TimelineEventCell => {
  return cell.type === 'event';
};

export const TimelineView = memo(() => {
  return (
    <Panel
      title="服务进度"
      css={`
        flex: 1;
        border-radius: 0;
      `}
      contentContainerStyle={{
        flex: 1,
      }}
    >
      <Content />
    </Panel>
  );
});

export const Content = memo(() => {
  const { taskNo, profile } = useTaskContext();
  const { rpx } = useResponsiveContext();
  const { loading, data, error } = useTaskTimeline(taskNo);
  const sections = useMemo(
    () =>
      buildTimeline(data ?? [], {
        isIndependentDeliveryCheckEnabled:
          profile.store.isIndependentDeliveryCheckEnabled,
      }),
    [data, profile.store.isIndependentDeliveryCheckEnabled],
  );
  const { cells } = useTaskTimelineModels({ sections });
  const items = useMemo(
    () =>
      cells
        .filter(isEventCell)
        .sort((x, y) => compareDesc(x.timestamp, y.timestamp)),
    [cells],
  );

  if (loading) {
    return (
      <Center
        css={`
          flex: 1;
        `}
      >
        <AnimatedLottieView
          source={AnimationSource.loadingCircle}
          loop
          autoPlay
          css={`
            width: ${rpx(180)}px;
          `}
        />
      </Center>
    );
  }

  if (error) {
    return (
      <Center
        css={`
          flex: 1;
        `}
      >
        <Icon
          name="exclamation-circle-o"
          size={rpx(50)}
          color={StatusColors.Danger}
        />
        <Typography.Label light color={StatusColors.Danger} size={rpx(28)}>
          加载工作进度失败!
        </Typography.Label>
      </Center>
    );
  }

  if (!items.length) {
    return (
      <Center
        css={`
          flex: 1;
        `}
      >
        <Icon
          name="exclamation-circle-o"
          size={rpx(28)}
          color="rgba(255, 255, 255, 0.3)"
        />
        <Typography.Label light color="rgba(255, 255, 255, 0.3)" size={rpx(28)}>
          暂无工作进度
        </Typography.Label>
      </Center>
    );
  }

  return (
    <LayoutProviderView
      css={`
        flex: 1;
      `}
    >
      {({ width }) => (
        <FlatList
          data={items}
          keyExtractor={x => x.id}
          renderItem={({ item, index }) => (
            <Cell
              item={item}
              width={width}
              index={index}
              last={index === items.length - 1}
            />
          )}
          showsVerticalScrollIndicator={false}
        />
      )}
    </LayoutProviderView>
  );
});

const ItemIconType: {
  [p in TimelineSectionType]: { name: IconType; size?: number; color: string };
} = {
  order: { name: 'clipboard', color: '#3aa17d' },
  'pre-inspection': { name: 'camera', color: '#06a7c4' },
  inspection: { name: 'oil-small', size: 16, color: '#2e3191' },
  construction: { name: 'tools', color: '#0054ae' },
  'delivery-check': { name: 'sheet-checked', color: '#662d8c' },
  'sampling-check': { name: 'sheet-checked', color: '#cf33a0' },
  quotation: { name: 'exclamation-circle-o', color: '#8e78ff' },
  none: { name: 'exclamation-circle-o', color: '#939393' },
  'order-rollback': { name: 'exclamation-circle-o', color: '#e82e2e' },
  'order-close': { name: 'exclamation-circle-o', color: '#f0802a' },
  'order-update': { name: 'exclamation-circle-o', color: '#4db4c9' },
  'order-finish': { name: 'exclamation-circle-o', color: '#28c538' },
};

const kSideWidth = 44;
const kIconSize = 24;
const kLineWidth = 2;
const kIconMargin = 12;

const Cell = memo(
  ({
    item,
    width,
    index,
    last,
  }: {
    item: TimelineEventCell;
    width: number;
    index: number;
    last?: boolean;
  }) => {
    const { rpx } = useResponsiveContext();

    const {
      name: iconName,
      color: iconColor,
      size: iconSize = rpx(kIconSize),
    } = ItemIconType[item.sectionType];

    const {
      task: {
        timeline: {
          connectorLineColor,
          titleTextColor,
          taglineTextColor,
          authorTextColor,
          timestampTextColor,
        },
      },
    } = usePresentationTheme();

    const shouldNotDisplayPlaceholder = (media: MediaObject) => {
      return media.type === 'image' && media.url.includes('#');
    };

    const sideWidth = rpx(kSideWidth);
    const lineWidth = rpx(kLineWidth);

    return (
      <MotiView
        from={{ opacity: 0, translateY: rpx(75) }}
        animate={{ opacity: 1, translateY: 0 }}
        transition={{ type: 'timing', duration: 150 }}
        delay={Math.min(1200, index * 50)}
        css={`
          flex-direction: row;
          justify-content: space-between;
          align-items: stretch;
        `}
      >
        {/** left side bar */}
        <View
          css={`
            width: ${sideWidth}px;
            flex-shrink: 0;
            flex-grow: 0;
            align-items: center;
          `}
        >
          <View
            css={`
              width: ${sideWidth}px;
              height: ${sideWidth}px;
              border-radius: ${sideWidth / 2}px;
              margin: ${rpx(kIconMargin)}px 0;
              background-color: ${iconColor};
              align-items: center;
              justify-content: center;
            `}
          >
            <Icon name={iconName} size={iconSize} color="#fff" />
          </View>
          <View
            css={`
              flex: 1;
              background-color: ${last ? 'transparent' : connectorLineColor};
              width: ${lineWidth}px;
              border-radius: ${lineWidth / 2}px;
            `}
          />
        </View>

        {/** details */}
        <View
          css={`
            flex: 1;
            padding-left: ${rpx(16)}px;
            padding-right: ${rpx(16)}px;
            padding-top: ${rpx(16)}px;
            margin-bottom: ${rpx(16)}px;
          `}
        >
          <Tagline
            tagline={item.title}
            numberOfLines={1}
            css={`
              line-height: ${rpx(38)}px;
              font-family: ${FontFamily.NotoSans.Light};
              font-weight: 300;
              font-size: ${rpx(22)}px;
              color: ${titleTextColor};
            `}
          />
          {item.taglines?.map((tagline, i) => (
            <Tagline
              key={i}
              tagline={tagline}
              numberOfLines={1}
              css={`
                line-height: ${rpx(28)}px;
                font-family: ${FontFamily.NotoSans.Light};
                font-weight: 300;
                font-size: ${rpx(20)}px;
                margin-bottom: ${rpx(8)}px;
                color: ${taglineTextColor};
              `}
            />
          ))}
          {item.extra ? (
            <View
              css={`
                margin-top: ${rpx(8)}px;
              `}
            >
              {item.extra}
            </View>
          ) : null}
          <HStack justifyContent="flex-start">
            <Typography.Label light color={authorTextColor} size={rpx(20)}>
              {item.technicianName}
            </Typography.Label>
            <Typography.Label
              light
              color={timestampTextColor}
              size={rpx(20)}
              css={`
                margin-left: ${rpx(16)}px;
              `}
            >
              {timeUtilNow(item.timestamp)}
            </Typography.Label>
          </HStack>
          <MediaCarousel
            display="list"
            listKey={item.id}
            numberOfItemsPerRow={4}
            itemSpacer={rpx(12)}
            medias={item.medias ?? []}
            listWidth={width - sideWidth - rpx(16) * 2 - rpx(8) - rpx(16)}
            noPlaceholder={shouldNotDisplayPlaceholder}
            itemStyle={{
              borderRadius: rpx(10),
              overflow: 'hidden',
              marginTop: rpx(10),
            }}
            playIconSize={rpx(40)}
          />
        </View>
      </MotiView>
    );
  },
);
